/**
 * Low-level HTTP client.
 * - Resolves URLs against VITE_API_BASE_URL.
 * - Throws ApiError on non-2xx so callers don't have to check response.ok.
 * - Supports AbortSignal so callers can cancel in-flight requests (used by
 *   useSearch to drop stale responses when filters change quickly).
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

let _token = null;

export function setAuthToken(token) { _token = token; }
export function clearAuthToken()    { _token = null; }

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Build a query string from an object. Skips null/undefined/empty-string values
 * and expands array values into repeated keys (FastAPI's default for List query
 * params, e.g. ?wealth_tier=A&wealth_tier=B).
 */
export function buildQuery(params) {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      for (const v of value) {
        if (v === null || v === undefined || v === '') continue;
        sp.append(key, String(v));
      }
    } else {
      sp.append(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export async function apiFetch(path, { method = 'GET', params, body, signal, headers } = {}) {
  const url = `${BASE_URL}${path}${buildQuery(params)}`;
  let res;
  try {
    // `body !== undefined` (not truthiness) so an explicit `null`/`0`/`''`
    // body — a legitimate JSON payload for e.g. clearing a scalar field to
    // null — still gets sent, rather than silently becoming a bodyless
    // request the way `body ? … : undefined` would treat it.
    const hasBody = body !== undefined;
    // A FormData body (multipart/form-data, e.g. the section-sources file
    // upload) must NOT be JSON-encoded and must NOT get an explicit
    // Content-Type — the browser sets `multipart/form-data; boundary=...`
    // itself, and overriding it here would drop the boundary and break parsing.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    res = await fetch(url, {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : null),
        ...(_token ? { Authorization: `Bearer ${_token}` } : null),
        ...headers,
      },
      body: hasBody ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(`Network error: ${err.message}`, { status: 0 });
  }

  let payload = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new Event('dg:unauthorized'));
    }
    const message =
      (payload && payload.detail && (payload.detail.msg || JSON.stringify(payload.detail))) ||
      `Request failed with status ${res.status}`;
    throw new ApiError(message, { status: res.status, body: payload });
  }

  return payload;
}
